/**
 * Destinations on the sky.
 *
 * `systemSky` already draws every system in the galaxy as a point of light in
 * its true direction, which makes the sky a navigation instrument rather than a
 * backdrop. This puts names on the handful of those points you could actually
 * go to, and says whether you could go there *now*.
 *
 * ── Why these are not `targetMarkers` ───────────────────────────────────────
 *
 * Body brackets answer "what is this thing I am pointing at", so they are
 * restricted to the middle of the screen and sized to the object. A jump target
 * has no size — it is a light at effectively infinite range — and the question
 * is the opposite one: "where are the places I can go", asked of the whole sky
 * at once. Same idea, different instrument, and bending one into the other
 * would have cost both of them their answer.
 *
 * Only drawn while the jump module is up, which is what keeps the sky clear the
 * rest of the time.
 */

import * as THREE from 'three';

import { WORLD_UNITS_PER_GALACTIC, type JumpReach } from '@interspacial/shared';

/**
 * Colour per state, and the whole point of the marker.
 *
 * Reachable reads as the station amber the rest of the HUD uses for things you
 * can act on. Everything else is deliberately cooler and dimmer: they are on
 * the sky so you can plan towards them, not so you can try them.
 */
const REACH_COLOURS: Record<JumpReach, string> = {
  ready: 'rgba(255, 212, 121, 0.95)',
  'out of reach': 'rgba(150, 170, 200, 0.55)',
  // Warm rather than the violet it used to be: violet now means "this is your
  // route", and two different ideas sharing a colour is worse than either of
  // them being the wrong shade.
  'needs a core': 'rgba(255, 176, 96, 0.8)',
  'not the gateway': 'rgba(150, 170, 200, 0.4)',
};

/**
 * The one colour that means "this is where you are going".
 *
 * Reserved. Everything else on the sky is a *fact* — reachable, too far, needs
 * a part — and this is the only thing that is a decision you already made. It
 * has to survive being glanced at among a dozen markers, which is what a colour
 * nothing else uses buys you.
 */
export const ROUTE_COLOUR = 'rgba(190, 120, 255, 0.98)';

export interface JumpMarkerTarget {
  systemId: string;
  name: string;
  /** Which cluster, named, when it is not the one we are in. */
  clusterName: string;
  sameCluster: boolean;
  reach: JumpReach;
  /** Why it cannot be jumped to, said plainly. */
  detail: string;
  distance: number;
  /** Unit direction from the ship, in world space. */
  direction: THREE.Vector3;
  /** The next hop on the planned route. Drawn in `ROUTE_COLOUR`. */
  onRoute?: boolean;
}

export interface JumpMarkerUpdate {
  camera: THREE.Camera;
  width: number;
  height: number;
  /**
   * Where the hull's nose points, as a unit direction in world space.
   *
   * The aim used to be measured from the middle of the screen, which is where
   * the *camera* points. Those are the same direction only while the head is
   * straight ahead — so under free look the offer to jump followed the player's
   * gaze rather than the ship, and could be had by looking at a marker while
   * flying somewhere else entirely.
   *
   * A jump is committed along the hull's axis. So the test is the angle between
   * that axis and the marker, in the world rather than on the screen, which also
   * means a marker off the edge of the view — or behind the camera while the
   * head is turned — is still aimed at if the ship is pointing at it.
   */
  nose: THREE.Vector3;
  /**
   * How far off the nose a marker counts as being aimed at, in pixels.
   *
   * Still in pixels, though it is now applied as an angle: this is a tolerance
   * that was tuned by looking at the thing, and converting it against the
   * camera's field of view keeps that feel exactly rather than restating it as
   * a number of degrees nobody has judged.
   *
   * Generous, because these are point sources at the far end of the sky and
   * asking a player to put a crosshair on a single pixel of star would be a
   * dexterity test rather than a navigation one.
   */
  aimWithin?: number;
  /** The destination being committed to, which is drawn as such. */
  confirming?: { systemId: string; progress: number } | null;
}

export interface JumpMarkers {
  /** Returns whichever reachable destination is under the reticle, if any. */
  update: (options: JumpMarkerUpdate) => string | null;
  setVisible: (visible: boolean) => void;
  /**
   * Which destination is the next hop on the planned route.
   *
   * Set after construction rather than baked in, because the route is changed
   * from the chart while the ship is in flight — and rebuilding every marker to
   * recolour one of them would mean rebuilding the scene they belong to.
   */
  /**
   * Mark the next hop on the sky.
   *
   * The *next* one, and deliberately not the destination. These markers are a
   * list of places this ship can jump to right now, so the only thing worth
   * colouring is which of them to pick — a violet pip three jumps away would be
   * pointing at somewhere you cannot go from here.
   */
  setRoute: (next: string | null) => void;
  dispose: () => void;
}

/**
 * How far out the markers are placed.
 *
 * Any distance works — they are projected, not lit — but it has to be well
 * inside the sky sphere or the camera's far plane clips them off the
 * destinations they are labelling.
 */
const MARKER_DISTANCE = 40_000;

/**
 * A galactic distance, in the units the rest of the HUD speaks.
 *
 * Converted rather than printed raw: galactic coordinates are a thousand world
 * units to one, so a neighbouring system that reads as "2.4 ku" in its own
 * units is 2.4 *million* of the units every other number on screen is in. Shown
 * unconverted it claimed the next star was nearer than the local gas giant.
 */
function galacticDistance(units: number): string {
  const world = units * WORLD_UNITS_PER_GALACTIC;

  if (world >= 1_000_000) return `${(world / 1_000_000).toFixed(1)} Mu`;
  if (world >= 1_000) return `${(world / 1_000).toFixed(1)} ku`;
  return `${Math.round(world)} u`;
}

export function createJumpMarkers(
  layer: HTMLElement,
  targets: readonly JumpMarkerTarget[],
): JumpMarkers {
  layer.replaceChildren();

  const markers = targets.map((target) => {
    const colour = target.onRoute ? ROUTE_COLOUR : REACH_COLOURS[target.reach];

    const root = document.createElement('div');
    root.style.cssText =
      'position:absolute;pointer-events:none;transform:translate(-50%,-50%);display:none';

    // A diamond rather than the bodies' square bracket: these are not things
    // you are pointing at, they are places, and they should not be mistaken for
    // something the ship could run into.
    //
    // Hollow, always. A filled pip sat exactly on top of the one thing it was
    // pointing at — the star is a point source, so the marker was covering the
    // whole of it, and you were aiming at a symbol rather than at a system.
    // Readiness is carried by the colour and the ring weight instead.
    const pip = document.createElement('div');
    pip.style.cssText = [
      'position:absolute',
      'left:50%',
      'top:50%',
      'width:11px',
      'height:11px',
      'margin:-6px 0 0 -6px',
      'transform:rotate(45deg)',
      `border:${target.reach === 'ready' ? 2 : 1}px solid ${colour}`,
      'background:transparent',
      target.reach === 'ready' ? `box-shadow:0 0 7px ${colour}` : '',
    ].join(';');
    root.appendChild(pip);

    const label = document.createElement('div');
    label.style.cssText = `position:absolute;bottom:100%;left:50%;transform:translateX(-50%);margin-bottom:9px;font-size:11px;white-space:nowrap;text-shadow:0 1px 3px #05070d;color:${colour};text-align:center`;

    const name = document.createElement('div');
    name.textContent = target.name;
    label.appendChild(name);

    // A crossing names the cluster it lands in, because that is the thing you
    // are choosing. A hop within your own does not — you already know.
    if (!target.sameCluster && target.clusterName) {
      const cluster = document.createElement('div');
      cluster.textContent = target.clusterName;
      cluster.style.cssText = 'font-size:9.5px;letter-spacing:1px;text-transform:uppercase;opacity:0.7';
      label.appendChild(cluster);
    }
    root.appendChild(label);

    const foot = document.createElement('div');
    foot.textContent =
      target.reach === 'ready'
        ? galacticDistance(target.distance)
        : `${galacticDistance(target.distance)} · ${target.detail}`;
    foot.style.cssText = `position:absolute;top:100%;left:50%;transform:translateX(-50%);margin-top:9px;font-size:9.5px;white-space:nowrap;font-variant-numeric:tabular-nums;text-shadow:0 1px 3px #05070d;opacity:0.8;color:${colour}`;
    root.appendChild(foot);

    // The commitment ring: a circle that fills as the confirm key is held, and
    // the only moving thing on the sky. Drawn with a conic gradient rather than
    // an arc so it is one element that reads as a dial rather than a progress
    // bar borrowed from a menu.
    const ring = document.createElement('div');
    ring.style.cssText = [
      'position:absolute',
      'left:50%',
      'top:50%',
      'width:34px',
      'height:34px',
      'margin:-17px 0 0 -17px',
      'border-radius:50%',
      'display:none',
      `border:1px solid ${colour}`,
    ].join(';');
    root.appendChild(ring);

    layer.appendChild(root);

    // Kept so the route colour can be applied and taken away again without
    // rebuilding the element — see `setRoute`.
    const painted = [pip, label, foot];
    return {
      root,
      ring,
      pip,
      painted,
      colour,
      target,
      aimed: false,
      routed: Boolean(target.onRoute),
    };
  });

  /*
   * A pointer to the route, when the route is not on screen.
   *
   * The markers themselves only exist where you are looking, which is fine for
   * "what is out there" and useless for "which way do I turn". One arrow, on
   * the rim, for the single target that is actually your next hop — more than
   * one and it stops being a direction and becomes a decoration.
   */
  const arrow = document.createElement('div');
  /*
   * A glyph rather than a CSS triangle.
   *
   * The triangle was chosen so it would point exactly where it was told rather
   * than wherever a font decided — sound reasoning, and it produced a 16px
   * wedge with no outline and no tail that read as a smear at the edge of the
   * screen. `↑` has a shaft, which is most of what makes an arrow legible.
   *
   * The alignment worry is handled rather than lived with: a fixed box, centred
   * text and `line-height:1` mean the glyph is rotated about the middle of a
   * known square instead of about whatever the baseline happened to be.
   */
  arrow.textContent = '↑';
  arrow.style.cssText = [
    'position:absolute',
    'pointer-events:none',
    'display:none',
    'width:22px',
    'height:22px',
    'line-height:22px',
    'text-align:center',
    'font-size:20px',
    `color:${ROUTE_COLOUR}`,
    `text-shadow:0 0 7px ${ROUTE_COLOUR}`,
  ].join(';');
  layer.appendChild(arrow);

  /**
   * Put the arrow on the rim, pointing outward along a direction.
   *
   * Takes normalised device coordinates rather than pixels, because that is
   * what a projection gives and because the direction is all that matters —
   * the magnitude of an off-screen point is unbounded and would otherwise have
   * to be clamped twice.
   *
   * Inset from the edge so the whole triangle is visible; a marker half off the
   * screen is a marker you have to work out rather than read.
   */
  const pointArrow = (ndcX: number, ndcY: number, width: number, height: number): void => {
    const angle = Math.atan2(-ndcY, ndcX);

    const inset = 34;
    const halfW = width / 2 - inset;
    const halfH = height / 2 - inset;

    // The rim of a rectangle along this bearing: whichever axis runs out first.
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const reach = Math.min(
      Math.abs(cos) < 1e-6 ? Infinity : halfW / Math.abs(cos),
      Math.abs(sin) < 1e-6 ? Infinity : halfH / Math.abs(sin),
    );

    arrow.style.display = '';
    arrow.style.left = `${width / 2 + cos * reach}px`;
    arrow.style.top = `${height / 2 + sin * reach}px`;
    // The triangle is drawn pointing up, so it is rotated to face outward.
    arrow.style.transform = `translate(-50%, -50%) rotate(${angle + Math.PI / 2}rad)`;
  };

  const at = new THREE.Vector3();
  const eye = new THREE.Vector3();
  let visible = false;

  const update = ({
    camera,
    width,
    height,
    nose,
    aimWithin = 70,
    confirming = null,
  }: JumpMarkerUpdate): string | null => {
    if (!visible) return null;

    camera.getWorldPosition(eye);

    let aimed: string | null = null;

    /*
     * The pixel tolerance, as the angle it subtends at this camera.
     *
     * Half the viewport height is the tangent of half the vertical field of
     * view, so a distance in pixels off the centre is an angle off the axis.
     * Doing the conversion here rather than hard-coding a few degrees means the
     * tolerance that was tuned by eye survives a change of field of view, and
     * that it is the same tolerance on a phone as on a desktop.
     */
    const perspective = camera as THREE.PerspectiveCamera;
    const fov = typeof perspective.fov === 'number' ? perspective.fov : 60;
    let closest = Math.atan(
      (aimWithin * Math.tan(THREE.MathUtils.degToRad(fov) / 2)) / (height / 2),
    );

    /*
     * Whether any marker on this frame *is* the route.
     *
     * Named for what it means. It was `routeOnScreen`, which is what it was
     * read as and not what it holds — it goes true when the route is found
     * whether it is on screen or behind you — and the line at the bottom that
     * hides the arrow depends on the difference.
     */
    let routeFound = false;

    for (const marker of markers) {
      /*
       * Aimed at, before anything about the screen is worked out.
       *
       * Deliberately above the projection and its early exits. The test used to
       * live down in the on-screen branch, so a marker behind the canopy could
       * not be aimed at — which was reasonable while the measurement was "near
       * the middle of the view" and is wrong now that it is "along the ship's
       * axis": the head turns freely, and where the pilot is looking has nothing
       * to do with where the hull is pointed.
       *
       * Only somewhere you could actually go can be aimed at. Letting the offer
       * settle on one that is out of reach would promise a commitment that could
       * not be made.
       */
      if (marker.target.reach === 'ready') {
        const off = marker.target.direction.angleTo(nose);
        if (off < closest) {
          closest = off;
          aimed = marker.target.systemId;
        }
      }

      // Placed relative to the camera every frame rather than parked in the
      // world: these are directions, not places, and at galactic range the
      // difference between "where the system is" and "which way it is" is the
      // entire ship's flight.
      at.copy(marker.target.direction).multiplyScalar(MARKER_DISTANCE).add(eye);
      at.project(camera);

      if (at.z > 1) {
        marker.root.style.display = 'none';

        /*
         * Behind the canopy. If it is the route, the arrow answers instead — and
         * the projected point is mirrored through the centre when a target is
         * behind you, so the sign has to be flipped to point the right way.
         *
         * `marker.routed`, not `marker.target.onRoute`. The second is what this
         * used to read and **nothing has ever set it**: it is an optional field
         * on the target that the flight view does not pass, so it was always
         * undefined, both arrow branches were dead, and the pointer could not
         * appear under any circumstances. The route is tracked by `setRoute`,
         * which writes `routed` — the same field the violet colouring uses, and
         * the reason that half worked while this half never did.
         */
        if (marker.routed) {
          pointArrow(-at.x, -at.y, width, height);
          routeFound = true;
        }
        continue;
      }

      const x = (at.x * 0.5 + 0.5) * width;
      const y = (-at.y * 0.5 + 0.5) * height;

      marker.root.style.display = '';
      marker.root.style.left = `${x}px`;
      marker.root.style.top = `${y}px`;

      // On screen and in front: the marker itself is the direction, so the rim
      // pointer stands down. Off to one side and it takes over.
      if (marker.routed) {
        routeFound = true;

        const onScreen = Math.abs(at.x) <= 1 && Math.abs(at.y) <= 1;
        if (onScreen) arrow.style.display = 'none';
        else pointArrow(at.x, at.y, width, height);
      }

      const committing = confirming?.systemId === marker.target.systemId;
      if (committing) {
        const sweep = Math.round(confirming!.progress * 360);
        marker.ring.style.display = '';
        marker.ring.style.background =
          `conic-gradient(${marker.colour} ${sweep}deg, transparent ${sweep}deg)`;
      } else if (marker.ring.style.display !== 'none') {
        marker.ring.style.display = 'none';
      }
    }

    // Under the reticle but not being drawn as such — the caller decides
    // whether that becomes a commitment.
    for (const marker of markers) {
      const lit = marker.target.systemId === aimed;
      if (lit === marker.aimed) continue;

      marker.aimed = lit;
      marker.root.style.filter = lit ? 'brightness(1.5)' : '';
    }

    // No route this frame — nothing for the rim to point at.
    if (!routeFound) arrow.style.display = 'none';

    return aimed;
  };

  const setRoute = (next: string | null): void => {
    for (const marker of markers) {
      const routed = next !== null && marker.target.systemId === next;
      if (routed === marker.routed) continue;

      marker.routed = routed;
      marker.colour = routed ? ROUTE_COLOUR : REACH_COLOURS[marker.target.reach];

      for (const part of marker.painted) part.style.color = marker.colour;

      // The pip is drawn with borders rather than text, so it is repainted
      // separately — and thickened when routed, since colour alone is a poor
      // signal against a sky of stars in the same family of hues.
      marker.pip.style.borderColor = marker.colour;
      marker.pip.style.borderWidth = routed || marker.target.reach === 'ready' ? '2px' : '1px';

      marker.pip.style.boxShadow = routed ? `0 0 9px ${marker.colour}` : '';
    }
  };

  return {
    update,
    setRoute,
    setVisible: (next: boolean) => {
      visible = next;
      if (next) return;

      arrow.style.display = 'none';
      for (const marker of markers) {
        marker.root.style.display = 'none';
        marker.ring.style.display = 'none';
      }
    },
    dispose: () => layer.replaceChildren(),
  };
}
